import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { getCurrentUser, fetchAuthSession, signOut as amplifySignOut, signIn, rememberDevice } from '@aws-amplify/auth';
import { jwtDecode } from 'jwt-decode';

interface DecodedIdToken {
  sub?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  'cognito:groups'?: string[];
  'custom:organizationId'?: string;
}

interface AdminUser {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  groups: string[];
  organizationId?: string;
}

interface AuthContextType {
  user: AdminUser | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<boolean>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  getAuthToken: () => Promise<string | null>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const ADMIN_GROUP_NAME = 'Admins';

function isUserAdmin(groups: string[]): boolean {
  return groups.some(g => g.toLowerCase() === ADMIN_GROUP_NAME.toLowerCase());
}

function parseGroups(groupsClaim: unknown): string[] {
  if (!groupsClaim) return [];
  if (typeof groupsClaim === 'string') {
    try {
      const parsed = JSON.parse(groupsClaim);
      return Array.isArray(parsed) ? parsed : [groupsClaim];
    } catch {
      return [groupsClaim];
    }
  }
  if (Array.isArray(groupsClaim)) {
    return groupsClaim;
  }
  return [];
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleSignOut = useCallback(async () => {
    try {
      await amplifySignOut();
      setUser(null);
      setError(null);
    } catch (err: any) {
      console.error('Error signing out:', err);
      setError(err.message || 'Failed to sign out');
    }
  }, []);

  const handleSignIn = useCallback(async (email: string, password: string, rememberMe: boolean = false): Promise<boolean> => {
    try {
      setError(null);
      setIsLoading(true);

      await signIn({ username: email, password });
      
      // If remember me is checked, register this device for 30 days
      if (rememberMe) {
        try {
          console.log('Remember me checked - registering device for 30 days');
          await rememberDevice();
          console.log('Device successfully registered for remember me');
        } catch (deviceError: any) {
          console.warn('Failed to register device for remember me:', deviceError);
          // Don't fail login if device registration fails
        }
      }
      
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No ID token received');
      }

      const decoded: DecodedIdToken = jwtDecode(idToken);
      const groups = parseGroups(decoded['cognito:groups']);

      if (!isUserAdmin(groups)) {
        await amplifySignOut();
        setError('Access denied. You must be an administrator to access this dashboard.');
        setIsLoading(false);
        return false;
      }

      setUser({
        userId: decoded.sub || '',
        email: decoded.email || '',
        firstName: decoded.given_name,
        lastName: decoded.family_name,
        groups,
        organizationId: decoded['custom:organizationId'],
      });

      setIsLoading(false);
      return true;

    } catch (err: any) {
      console.error('Sign in error:', err);
      setError(err.message || 'Failed to sign in');
      setIsLoading(false);
      return false;
    }
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      setError(null);
      await getCurrentUser();
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken?.toString();

      if (idToken) {
        const decoded: DecodedIdToken = jwtDecode(idToken);
        const groups = parseGroups(decoded['cognito:groups']);

        if (!isUserAdmin(groups)) {
          await amplifySignOut();
          setUser(null);
          setError('Access denied. Administrator privileges required.');
          return;
        }

        setUser({
          userId: decoded.sub || '',
          email: decoded.email || '',
          firstName: decoded.given_name,
          lastName: decoded.family_name,
          groups,
          organizationId: decoded['custom:organizationId'],
        });
      } else {
        setUser(null);
      }
    } catch (err) {
      console.info('Session refresh failed (not authenticated):', err);
      setUser(null);
    }
  }, []);

  const getAuthToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  }, []);

  // Initial auth check
  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);
      try {
        await refreshSession();
      } catch {
        // Not authenticated
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, [refreshSession]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isAdmin: user ? isUserAdmin(user.groups) : false,
    isLoading,
    error,
    signIn: handleSignIn,
    signOut: handleSignOut,
    refreshSession,
    getAuthToken,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

